import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  PlayIcon,
  ClockIcon,
  EyeIcon,
  HeartIcon,
  ShareIcon,
  BookmarkIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  LanguageIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import {
  StarIcon as StarSolidIcon,
  HeartIcon as HeartSolidIcon,
} from "@heroicons/react/24/solid";
import { useBlogs } from "../../contexts/BlogContext";
import { Blog } from "../../firebase/blogService";

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { blogs, getRelatedBlogPosts, incrementViews } = useBlogs();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [tableOfContents, setTableOfContents] = useState<
    { id: string; text: string; level: number }[]
  >([]);
  const [activeSection, setActiveSection] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (slug) {
      const foundBlog = blogs.find((b) => b.slug === slug && b.published);
      if (foundBlog) {
        setBlog(foundBlog);
        // Increment views
        incrementViews(foundBlog.id!);

        // Load related blogs
        getRelatedBlogPosts(foundBlog.id!, 3).then(setRelatedBlogs);
      }
      setLoading(false);
    }
  }, [slug, blogs, incrementViews, getRelatedBlogPosts]);

  useEffect(() => {
    const handleScroll = () => {
      if (mainContentRef.current) {
        const element = mainContentRef.current;
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const documentHeight = element.scrollHeight;

        // Calculate reading progress based on main content position
        const scrolled = Math.max(0, -rect.top);
        const maxScroll = documentHeight - windowHeight;
        const progress = maxScroll > 0 ? (scrolled / maxScroll) * 100 : 0;
        setReadingProgress(Math.min(Math.max(progress, 0), 100));
      }

      // Track active section for table of contents
      const sections = document.querySelectorAll("h1[id], h2[id], h3[id]");
      let currentActiveSection = "";

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150) {
          currentActiveSection = section.id;
        }
      });

      if (currentActiveSection !== activeSection) {
        setActiveSection(currentActiveSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // Extract table of contents from blog content
  useEffect(() => {
    if (blog?.content) {
      const headingRegex = /^(#{1,3})\s+(.+)$/gm;
      const toc: { id: string; text: string; level: number }[] = [];
      let match;

      while ((match = headingRegex.exec(blog.content)) !== null) {
        const level = match[1].length;
        const text = match[2];
        const id = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        toc.push({ id, text, level });
      }

      setTableOfContents(toc);
    }
  }, [blog?.content]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "Advanced":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  const handleShare = async () => {
    if (navigator.share && blog) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Error sharing:", error);
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    // In a real app, you'd make an API call here
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    // In a real app, you'd save to user's bookmarks
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Blog Post Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            The tutorial you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/blogs"
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-2" />
            Back to Tutorials
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-700 z-50">
        <div
          className="h-full bg-gradient-to-r from-indigo-600 to-blue-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Main Container */}
      <div
        className="relative bg-white dark:bg-gray-800 pt-16"
        ref={mainContentRef}
      >
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link
              to="/blogs"
              className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Back to Tutorials
            </Link>
          </nav>

          {/* Blog Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            {/* Categories and Badges */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
                {blog.category}
              </span>
              {blog.subcategory && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  • {blog.subcategory}
                </span>
              )}
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(
                  blog.difficulty
                )}`}
              >
                <AcademicCapIcon className="h-4 w-4 mr-1" />
                {blog.difficulty}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                <LanguageIcon className="h-4 w-4 mr-1" />
                {blog.language}
              </span>
              {blog.featured && (
                <StarSolidIcon className="h-6 w-6 text-yellow-400" />
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {blog.excerpt}
            </p>
          </motion.div>

          {/* Table of Contents - Mobile */}
          {tableOfContents.length > 0 && (
            <div className="lg:hidden mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 shadow-lg border border-indigo-200 dark:border-indigo-800"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                    <div className="bg-indigo-600 rounded-lg p-2 mr-3">
                      <DocumentTextIcon className="h-5 w-5 text-white" />
                    </div>
                    📚 Table of Contents
                  </h3>
                  <span className="text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900 px-2 py-1 rounded-full">
                    {tableOfContents.length} sections
                  </span>
                </div>
                <nav className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tableOfContents.map((item, index) => (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                      onClick={() => scrollToSection(item.id)}
                      className={`group relative w-full text-left p-3 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                        activeSection === item.id
                          ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/25"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 shadow-md hover:shadow-lg"
                      }`}
                      style={{ paddingLeft: `${(item.level - 1) * 12 + 16}px` }}
                    >
                      <div className="flex items-center">
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-3 transition-colors ${
                            activeSection === item.id
                              ? "bg-white"
                              : item.level === 1
                              ? "bg-indigo-600"
                              : item.level === 2
                              ? "bg-blue-500"
                              : "bg-purple-500"
                          }`}
                        />
                        <span className="text-sm font-medium truncate">
                          {item.text}
                        </span>
                        {activeSection === item.id && (
                          <span className="ml-auto text-xs opacity-75">
                            Reading
                          </span>
                        )}
                      </div>
                      {item.level === 1 && (
                        <div
                          className={`absolute left-0 top-0 w-1 h-full rounded-l-xl transition-colors ${
                            activeSection === item.id
                              ? "bg-white"
                              : "bg-indigo-600"
                          }`}
                        />
                      )}
                    </motion.button>
                  ))}
                </nav>
              </motion.div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Desktop Sidebar */}
            {tableOfContents.length > 0 && (
              <div className="lg:col-span-1 order-2 lg:order-1 hidden lg:block">
                <div className="sticky top-24">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700 backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg p-2 mr-3 shadow-lg">
                          <DocumentTextIcon className="h-5 w-5 text-white" />
                        </div>
                        <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                          📚 Contents
                        </span>
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900 px-2 py-1 rounded-full font-medium">
                          {tableOfContents.length}
                        </span>
                      </div>
                    </div>

                    <div className="h-px bg-gradient-to-r from-transparent via-indigo-200 dark:via-indigo-800 to-transparent mb-6" />

                    <nav className="toc-scroll max-h-96 overflow-y-auto pr-2 space-y-1">
                      <style>{`
                        .toc-scroll::-webkit-scrollbar {
                          width: 4px;
                        }
                        .toc-scroll::-webkit-scrollbar-track {
                          background: transparent;
                        }
                        .toc-scroll::-webkit-scrollbar-thumb {
                          background: rgb(129 140 248);
                          border-radius: 2px;
                        }
                        .toc-scroll::-webkit-scrollbar-thumb:hover {
                          background: rgb(99 102 241);
                        }
                        .dark .toc-scroll::-webkit-scrollbar-thumb {
                          background: rgb(67 56 202);
                        }
                      `}</style>
                      {tableOfContents.map((item, index) => (
                        <motion.button
                          key={item.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          onClick={() => scrollToSection(item.id)}
                          className={`group relative w-full text-left p-3 rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${
                            activeSection === item.id
                              ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-600/25"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 dark:hover:from-indigo-900/30 dark:hover:to-blue-900/30 hover:text-indigo-600 dark:hover:text-indigo-400"
                          }`}
                          style={{
                            paddingLeft: `${(item.level - 1) * 16 + 16}px`,
                          }}
                        >
                          <div className="flex items-center">
                            <div className="flex items-center mr-3">
                              <span
                                className={`inline-block w-2 h-2 rounded-full transition-all duration-300 ${
                                  activeSection === item.id
                                    ? "bg-white shadow-lg"
                                    : item.level === 1
                                    ? "bg-indigo-600 group-hover:bg-indigo-500"
                                    : item.level === 2
                                    ? "bg-blue-500 group-hover:bg-blue-400"
                                    : "bg-purple-500 group-hover:bg-purple-400"
                                }`}
                              />
                              {item.level === 1 && (
                                <div
                                  className={`w-4 h-px ml-1 transition-colors ${
                                    activeSection === item.id
                                      ? "bg-white"
                                      : "bg-indigo-300 dark:bg-indigo-700"
                                  }`}
                                />
                              )}
                            </div>
                            <span
                              className={`text-sm font-medium transition-all duration-300 ${
                                activeSection === item.id
                                  ? "font-semibold"
                                  : "group-hover:font-medium"
                              }`}
                            >
                              {item.text}
                            </span>
                            {activeSection === item.id && (
                              <div className="ml-auto flex items-center">
                                <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-2" />
                                <span className="text-xs opacity-90 font-medium">
                                  Reading
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Progress indicator for active section */}
                          {activeSection === item.id && (
                            <div className="absolute left-0 top-0 w-1 h-full bg-white rounded-l-xl shadow-lg" />
                          )}

                          {/* Hover effect indicator */}
                          <div
                            className={`absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-indigo-600 to-blue-600 rounded-l-xl transform transition-all duration-300 ${
                              activeSection === item.id
                                ? "opacity-0"
                                : "opacity-0 group-hover:opacity-50"
                            }`}
                          />
                        </motion.button>
                      ))}
                    </nav>

                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                        <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
                          Click to navigate
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {/* Main Content */}
            <div
              className={`${
                tableOfContents.length > 0 ? "lg:col-span-3" : "lg:col-span-4"
              } order-1 lg:order-2`}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                {/* Featured Image - Only show if no video and no gallery */}
                {blog.featuredImage &&
                  !blog.youtubeVideoId &&
                  (!blog.gallery || blog.gallery.length === 0) && (
                    <div className="relative h-64 md:h-96 overflow-hidden">
                      <img
                        src={blog.featuredImage}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                {/* Gallery */}
                {blog.gallery && blog.gallery.length > 0 && (
                  <div className="p-6 md:p-8 lg:p-12 pb-0">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="mb-6"
                    >
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center space-x-3 mb-6">
                          <DocumentTextIcon className="h-6 w-6 text-purple-600" />
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            🖼️ Image Gallery
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {blog.gallery.map((image, index) => (
                            <div
                              key={index}
                              className="relative h-56 rounded-xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                              <img
                                src={image}
                                alt={`Gallery item ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                )}

                <div className="p-6 md:p-8 lg:p-12">
                  {/* YouTube Video Section */}
                  {blog.youtubeVideoId && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="mb-10"
                    >
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl p-8 border border-red-200 dark:border-red-800">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="bg-red-600 rounded-lg p-2">
                            <PlayIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                              📺 Video Tutorial
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Watch the complete tutorial on YouTube
                            </p>
                          </div>
                        </div>

                        {blog.youtubeVideoTitle && (
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {blog.youtubeVideoTitle}
                          </h4>
                        )}

                        {blog.youtubeVideoDescription && (
                          <p className="text-gray-600 dark:text-gray-300 mb-4">
                            {blog.youtubeVideoDescription}
                          </p>
                        )}

                        {showVideoPlayer ? (
                          <div className="relative w-full h-0 pb-[56.25%] rounded-xl overflow-hidden">
                            <iframe
                              src={`https://www.youtube.com/embed/${blog.youtubeVideoId}?autoplay=1`}
                              title={blog.youtubeVideoTitle || blog.title}
                              className="absolute top-0 left-0 w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <div
                            className="relative cursor-pointer group"
                            onClick={() => setShowVideoPlayer(true)}
                          >
                            <div className="relative w-full h-0 pb-[56.25%] rounded-xl overflow-hidden">
                              <img
                                src={
                                  blog.youtubeVideoThumbnail ||
                                  `https://img.youtube.com/vi/${blog.youtubeVideoId}/maxresdefault.jpg`
                                }
                                alt="Video thumbnail"
                                className="absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-20 transition-all duration-300">
                                <div className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform duration-300">
                                  <PlayIcon className="h-8 w-8 text-white" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex space-x-4">
                          <a
                            href={`https://www.youtube.com/watch?v=${blog.youtubeVideoId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <PlayIcon className="h-4 w-4 mr-2" />
                            Watch on YouTube
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  {/* Meta Information */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500 dark:text-gray-400 mb-10 p-6 bg-gray-50 dark:bg-gray-900 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-4 w-4" />
                      <span>by {blog.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(blog.publishedDate || "")}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4" />
                      <span>{blog.readingTime} min read</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <EyeIcon className="h-4 w-4" />
                      <span>{formatNumber(blog.views || 0)} views</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <HeartIcon className="h-4 w-4" />
                      <span>{blog.likes || 0} likes</span>
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-4 mb-10">
                    <button
                      onClick={handleLike}
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium transition-colors ${
                        isLiked
                          ? "text-red-600 border-red-300 bg-red-50 dark:bg-red-900/20"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {isLiked ? (
                        <HeartSolidIcon className="h-4 w-4 mr-2" />
                      ) : (
                        <HeartIcon className="h-4 w-4 mr-2" />
                      )}
                      Like
                    </button>

                    <button
                      onClick={handleBookmark}
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium transition-colors ${
                        isBookmarked
                          ? "text-indigo-600 border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <BookmarkIcon className="h-4 w-4 mr-2" />
                      {isBookmarked ? "Bookmarked" : "Bookmark"}
                    </button>

                    <button
                      onClick={handleShare}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ShareIcon className="h-4 w-4 mr-2" />
                      Share
                    </button>

                    {blog.codeRepository && (
                      <a
                        href={blog.codeRepository}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <CodeBracketIcon className="h-4 w-4 mr-2" />
                        Source Code
                      </a>
                    )}

                    {blog.demoUrl && (
                      <a
                        href={blog.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                        Live Demo
                      </a>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-3 mb-12 p-6 bg-indigo-50 dark:bg-indigo-900/10 rounded-xl">
                    {blog.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-sm font-medium shadow-sm hover:shadow-md transition-shadow"
                      >
                        <TagIcon className="h-3 w-3 mr-2" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Blog Content */}
                  <motion.div
                    ref={contentRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mb-16"
                  >
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3 mb-8">
                        <DocumentTextIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          📖 Tutorial Content
                        </h3>
                      </div>
                      <div className="prose prose-lg dark:prose-invert w-full max-w-full prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-indigo-600 dark:prose-code:text-indigo-400 prose-pre:bg-gray-800 prose-pre:text-gray-100">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            // Custom component for YouTube videos
                            p: ({ children }) => {
                              const text = children?.toString() || "";
                              const youtubeMatch =
                                text.match(/\[youtube\]\((.*?)\)/);
                              if (youtubeMatch) {
                                const videoId = youtubeMatch[1];
                                return (
                                  <div className="my-6 relative w-full h-0 pb-[56.25%] rounded-xl overflow-hidden">
                                    <iframe
                                      src={`https://www.youtube.com/embed/${videoId}`}
                                      title="YouTube video"
                                      className="absolute top-0 left-0 w-full h-full"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </div>
                                );
                              }
                              return (
                                <p className="leading-relaxed">{children}</p>
                              );
                            },
                            // Custom styling for code blocks
                            code: ({ className, children, ...props }: any) => {
                              const match = /language-(\w+)/.exec(
                                className || ""
                              );
                              const isInline = !match && !className;
                              return !isInline ? (
                                <div className="relative">
                                  {match && (
                                    <div className="absolute top-0 right-0 bg-gray-700 text-gray-300 px-3 py-1 text-xs rounded-bl-lg">
                                      {match[1]}
                                    </div>
                                  )}
                                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                                    <code className={className} {...props}>
                                      {children}
                                    </code>
                                  </pre>
                                </div>
                              ) : (
                                <code
                                  className="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 px-1 py-0.5 rounded text-sm"
                                  {...props}
                                >
                                  {children}
                                </code>
                              );
                            },
                            // Custom styling for blockquotes (alerts)
                            blockquote: ({ children }) => (
                              <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4 my-4 rounded-r-lg">
                                <div className="text-blue-800 dark:text-blue-200">
                                  {children}
                                </div>
                              </div>
                            ),
                            // Custom styling for tables
                            table: ({ children }) => (
                              <div className="overflow-x-auto my-6">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                                  {children}
                                </table>
                              </div>
                            ),
                            thead: ({ children }) => (
                              <thead className="bg-gray-50 dark:bg-gray-800">
                                {children}
                              </thead>
                            ),
                            tbody: ({ children }) => (
                              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {children}
                              </tbody>
                            ),
                            th: ({ children }) => (
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {children}
                              </th>
                            ),
                            td: ({ children }) => (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {children}
                              </td>
                            ),
                            // Custom styling for headings
                            h1: ({ children }) => {
                              const text = children?.toString() || "";
                              const id = text
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "");
                              return (
                                <h1
                                  id={id}
                                  className="text-3xl font-bold text-gray-900 dark:text-white mt-8 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 scroll-mt-24"
                                >
                                  {children}
                                </h1>
                              );
                            },
                            h2: ({ children }) => {
                              const text = children?.toString() || "";
                              const id = text
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "");
                              return (
                                <h2
                                  id={id}
                                  className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 scroll-mt-24"
                                >
                                  {children}
                                </h2>
                              );
                            },
                            h3: ({ children }) => {
                              const text = children?.toString() || "";
                              const id = text
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "");
                              return (
                                <h3
                                  id={id}
                                  className="text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3 scroll-mt-24"
                                >
                                  {children}
                                </h3>
                              );
                            },
                            // Custom styling for links
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {blog.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </motion.div>

                  {/* Downloadable Resources */}
                  {blog.downloadableResources &&
                    blog.downloadableResources.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mb-16"
                      >
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-8 border border-purple-200 dark:border-purple-800">
                          <div className="flex items-center space-x-3 mb-6">
                            <ArrowDownTrayIcon className="h-6 w-6 text-purple-600" />
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                              📦 Downloads & Resources
                            </h3>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {blog.downloadableResources.map(
                              (resource, index) => (
                                <a
                                  key={index}
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 hover:scale-105"
                                >
                                  <ArrowDownTrayIcon className="h-6 w-6 text-purple-600 flex-shrink-0" />
                                  <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {resource.title}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                                      {resource.type} file
                                    </div>
                                  </div>
                                </a>
                              )
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}

                  {/* Author Info */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mb-16"
                  >
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8">
                      <div className="flex items-center space-x-6">
                        {blog.authorImage ? (
                          <img
                            src={blog.authorImage}
                            alt={blog.author}
                            className="w-20 h-20 rounded-full object-cover shadow-lg"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <UserIcon className="h-10 w-10 text-white" />
                          </div>
                        )}
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                            {blog.author}
                          </h4>
                          {blog.authorBio && (
                            <p className="text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                              {blog.authorBio}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Related Blogs */}
                  {relatedBlogs.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                    >
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                        Related Tutorials
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {relatedBlogs.map((relatedBlog) => (
                          <Link
                            key={relatedBlog.id}
                            to={`/blogs/${relatedBlog.slug}`}
                            className="block bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105"
                          >
                            <div className="relative h-40 overflow-hidden">
                              {relatedBlog.featuredImage ? (
                                <img
                                  src={relatedBlog.featuredImage}
                                  alt={relatedBlog.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                                  <DocumentTextIcon className="h-8 w-8 text-white" />
                                </div>
                              )}
                              {relatedBlog.youtubeVideoId && (
                                <div className="absolute top-2 left-2">
                                  <div className="bg-red-600 text-white px-2 py-1 rounded flex items-center space-x-1 text-xs">
                                    <PlayIcon className="h-3 w-3" />
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="p-6">
                              <h4 className="font-bold text-gray-900 dark:text-white line-clamp-2 mb-3 text-lg">
                                {relatedBlog.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mb-4">
                                {relatedBlog.excerpt}
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>{relatedBlog.readingTime} min read</span>
                                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded-full">
                                  {relatedBlog.difficulty}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Action Buttons */}
        <div className="fixed right-6 bottom-6 space-y-3">
          <button
            onClick={handleLike}
            className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all ${
              isLiked
                ? "bg-red-600 text-white"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:shadow-xl"
            }`}
          >
            {isLiked ? (
              <HeartSolidIcon className="h-6 w-6" />
            ) : (
              <HeartIcon className="h-6 w-6" />
            )}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-lg hover:shadow-xl transition-all"
          >
            <ShareIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
